import React, { useState } from "react";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import { DotLegalDatePicker, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "../../../../localization/useTranslation";
import { fieldLineHeight } from "@common/components/tableDisplayFields/tableDisplayFieldsPane/TableDisplayFieldsPane.styles";

export interface StatusTimelineConfig {
    referenceMonth: string;
    monthsBack: number;
    monthsForward: number;
}

interface StatusTimelineFieldConfigProps {
    fieldName: string;
    configRef: React.MutableRefObject<StatusTimelineConfig | null>;
}

const monthsOptions = [
    { id: "0", name: "0" },
    { id: "3", name: "3" },
    { id: "6", name: "6" },
    { id: "9", name: "9" },
    { id: "12", name: "12" },
];

function StatusTimelineFieldConfig(props: StatusTimelineFieldConfigProps) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();

    // Seeded from the ref rather than the saved config: the pane remounts this row when a search hides and shows it
    // again, and it must come back with the settings Apply is going to save.
    const pendingConfig = props.configRef.current;
    const [isExpanded, setIsExpanded] = useState(false);
    const [referenceMonth, setReferenceMonth] = useState<Date | null>(pendingConfig ? new Date(pendingConfig.referenceMonth) : new Date());
    const [monthsBack, setMonthsBack] = useState<string | null>(pendingConfig?.monthsBack.toString() ?? "6");
    const [monthsForward, setMonthsForward] = useState<string | null>(pendingConfig?.monthsForward.toString() ?? "0");

    function updateConfig(updates: { ref?: Date | null; back?: string | null; fwd?: string | null }) {
        const ref = updates.ref !== undefined ? updates.ref : referenceMonth;
        const back = updates.back !== undefined ? updates.back : monthsBack;
        const fwd = updates.fwd !== undefined ? updates.fwd : monthsForward;
        props.configRef.current = {
            referenceMonth: (ref ?? new Date()).toISOString(),
            monthsBack: parseInt(back ?? "6"),
            monthsForward: parseInt(fwd ?? "0"),
        };
    }

    if (!props.configRef.current) {
        updateConfig({});
    }

    return (
        <>
            <Box
                sx={{
                    height: fieldLineHeight,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                {/* Styled like the checkbox labels on the other column rows, which this name sits among */}
                <Typography sx={(theme) => ({ flex: 1, fontSize: theme.typography.pxToRem(14), color: theme.palette.primary.dark })}>
                    {props.fieldName}
                </Typography>
                <IconButton size="small" sx={{ marginRight: 1 }}>
                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </Box>

            <Collapse in={isExpanded}>
                <Box sx={{ padding: 2, paddingTop: 0 }}>
                    <DotLegalDatePicker
                        label={translateString("month")}
                        value={referenceMonth}
                        onChange={(date) => {
                            setReferenceMonth(date);
                            updateConfig({ ref: date });
                        }}
                        view="month"
                        language={getDateTimePickerLanguage()}
                    />
                    <DotLegalHeader marginBottom={0} headerStyle="extraSmall">
                        {translateString("interval")}
                    </DotLegalHeader>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <DotLegalSelect
                            label={translateString("monthsBefore")}
                            options={monthsOptions}
                            selectedItem={monthsBack}
                            onChange={(value) => {
                                setMonthsBack(value);
                                updateConfig({ back: value });
                            }}
                            noOptionsLabel={translateString("noOptions")}
                            disableClearable
                        />
                        <DotLegalSelect
                            label={translateString("monthsAfter")}
                            options={monthsOptions}
                            selectedItem={monthsForward}
                            onChange={(value) => {
                                setMonthsForward(value);
                                updateConfig({ fwd: value });
                            }}
                            noOptionsLabel={translateString("noOptions")}
                            disableClearable
                        />
                    </Box>
                </Box>
            </Collapse>
        </>
    );
}

export default StatusTimelineFieldConfig;
